/* =========================================================
   Reports — export. Excel and CSV reuse the shared writers;
   PDF opens a print view the browser turns into a file.
   ========================================================= */
import type { Lang } from '@/types';
import { COLS, L, type Category, type ReportRow } from './model';

export interface ExportTable {
  header: string[];
  data: string[][];
}

/** Header labels + the visible columns of every row, in picker order. */
export function buildExport(category: Category, rows: ReportRow[], cols: string[], lang: Lang): ExportTable {
  const defs = COLS[category].filter((c) => cols.includes(c.key));
  return {
    header: defs.map((c) => L(lang, c.es, c.en)),
    data: rows.map((r) => defs.map((c) => {
      const v = c.get(r, lang);
      return v == null ? '' : String(v);
    })),
  };
}

const escapeHtml = (v: unknown) =>
  String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** The print window is a blank document, so it inherits none of the app's
 *  styles. Nunito is bundled rather than served from a CDN, so its
 *  @font-face rules are copied across with their urls made absolute. */
function fontFaceCss(): string {
  const out: string[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // cross-origin sheet
    }
    for (const rule of Array.from(rules)) {
      if (rule.constructor.name !== 'CSSFontFaceRule' && !/^@font-face/.test(rule.cssText)) continue;
      out.push(
        rule.cssText.replace(/url\((['"]?)([^'")]+)\1\)/g, (m, q, raw: string) => {
          try {
            return `url("${new URL(raw, sheet.href || location.href).href}")`;
          } catch {
            return m;
          }
        }),
      );
    }
  }
  return out.join('\n');
}

/** Opens a formatted, print-ready view in a new tab. Returns false when the
 *  browser blocked the popup. */
export function openPrint(title: string, subtitle: string, header: string[], data: string[][], lang: Lang): boolean {
  const w = window.open('', '_blank');
  if (!w) return false;

  const headHtml = header.map((h) => '<th>' + escapeHtml(h) + '</th>').join('');
  const rowsHtml = data
    .map(
      (row, i) =>
        '<tr class="' + (i % 2 ? 'alt' : '') + '">' +
        row.map((c) => '<td>' + escapeHtml(c || '—') + '</td>').join('') +
        '</tr>',
    )
    .join('');

  const generated = L(lang, 'Generado: ', 'Generated: ') + new Date().toLocaleString(lang === 'es' ? 'es-MX' : 'en-US');
  const records = data.length + ' ' + L(lang, 'registros', 'records');
  const kicker = L(lang, 'Reporte de asociados', 'Partner report');
  const footer = L(
    lang,
    'ETC Iberoamérica MCO · Documento generado desde ETC Connect',
    'ETC Iberoamérica MCO · Document generated from ETC Connect',
  );

  const doc =
    '<!doctype html><html lang="' + lang + '"><head><meta charset="utf-8"><title>' + escapeHtml(title) + '</title>' +
    '<style>' + fontFaceCss() +
    '*{box-sizing:border-box}' +
    "body{font-family:'Nunito Variable',Nunito,system-ui,sans-serif;color:#1B2330;margin:0;padding:32px 36px;}" +
    '.hd{display:flex;align-items:flex-end;justify-content:space-between;border-bottom:3px solid #F26522;padding-bottom:14px;margin-bottom:6px;}' +
    '.brand{font-size:22px;font-weight:800;letter-spacing:-.01em;}' +
    '.brand small{display:block;font-size:11px;font-weight:700;color:#7A8597;letter-spacing:.14em;text-transform:uppercase;margin-top:2px;}' +
    '.meta{text-align:right;font-size:11px;color:#7A8597;}' +
    'h1{font-size:17px;font-weight:700;margin:16px 0 2px;}' +
    '.sub{font-size:12px;color:#4B5563;margin-bottom:14px;}' +
    'table{width:100%;border-collapse:collapse;font-size:10.5px;}' +
    'th{background:#D97757;color:#fff;text-align:left;padding:7px 9px;font-size:9px;text-transform:uppercase;letter-spacing:.05em;font-weight:700;}' +
    'td{padding:6px 9px;border-bottom:1px solid #E5E7EC;vertical-align:top;}' +
    'tr.alt td{background:#FAFAFB;}' +
    '.ft{margin-top:16px;font-size:10px;color:#9aa3b2;text-align:center;}' +
    '@media print{body{padding:0;}.hd{padding:0 0 14px;}}' +
    '</style></head><body>' +
    '<div class="hd"><div class="brand">ETC Connect<small>' + escapeHtml(kicker) + '</small></div>' +
    '<div class="meta">' + escapeHtml(generated) + '<br>' + escapeHtml(records) + '</div></div>' +
    '<h1>' + escapeHtml(title) + '</h1><div class="sub">' + escapeHtml(subtitle) + '</div>' +
    '<table><thead><tr>' + headHtml + '</tr></thead><tbody>' + rowsHtml + '</tbody></table>' +
    '<div class="ft">' + escapeHtml(footer) + '</div>' +
    '</body></html>';

  w.document.open();
  w.document.write(doc);
  w.document.close();
  w.focus();
  setTimeout(() => {
    try {
      w.print();
    } catch {
      /* the user can still print from the tab */
    }
  }, 500);
  return true;
}
